// artPrimitives.ts — shared canvas colour + shape helpers, ported (behaviour-identical) from the headless
// art generators in `art-gen/export-*.js`. Those render the game's sprites to PNG with @napi-rs/canvas;
// the same code runs against the browser's CanvasRenderingContext2D, so the level editor draws cells
// procedurally (live, no PNG loading) with the exact same look as the game art. Read-only mirror of
// art-gen — no `Assets/` changes.
// "#RRGGBB" -> {r,g,b} (0..255).
export function hexToRgb(hex) {
    const n = parseInt(hex.slice(1), 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}
function rgbToHsl(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;
    const mx = Math.max(r, g, b);
    const mn = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (mx + mn) / 2;
    const d = mx - mn;
    if (d) {
        s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
        if (mx === r)
            h = (g - b) / d + (g < b ? 6 : 0);
        else if (mx === g)
            h = (b - r) / d + 2;
        else
            h = (r - g) / d + 4;
        h /= 6;
    }
    return { h, s, l };
}
function hslToRgb(h, s, l) {
    let r;
    let g;
    let b;
    if (s === 0) {
        r = g = b = l;
    }
    else {
        const f = (p, q, t) => {
            if (t < 0)
                t += 1;
            if (t > 1)
                t -= 1;
            if (t < 1 / 6)
                return p + (q - p) * 6 * t;
            if (t < 1 / 2)
                return q;
            if (t < 2 / 3)
                return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = f(p, q, h + 1 / 3);
        g = f(p, q, h);
        b = f(p, q, h - 1 / 3);
    }
    return { r: Math.round(r * 255), g: Math.round(g * 255), b: Math.round(b * 255) };
}
const clamp01 = (v) => Math.max(0, Math.min(1, v));
// Lighten/darken a hex by `dl` (lightness delta), optionally shifting saturation by `ds`. Returns an
// `rgb(...)` string ready for fill/strokeStyle. The workhorse shading call used across the art.
export function shade(hex, dl, ds = 0) {
    const { r, g, b } = hexToRgb(hex);
    const h = rgbToHsl(r, g, b);
    const c = hslToRgb(h.h, clamp01(h.s + ds), clamp01(h.l + dl));
    return `rgb(${c.r},${c.g},${c.b})`;
}
// Traces a rounded-rectangle path (does NOT fill/stroke). `r` is clamped to half the shorter side.
export function roundRect(ctx, x, y, w, h, r) {
    r = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}
// Traces an axis-aligned ellipse path.
export function ellipse(ctx, cx, cy, rx, ry) {
    ctx.beginPath();
    ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    ctx.closePath();
}
